import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, MoreHorizontal } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api";

const RoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [menus, setMenus] = useState([]);
    const [privileges, setPrivileges] = useState({});
    const [expandedMenus, setExpandedMenus] = useState({});
    const [selectedPrivileges, setSelectedPrivileges] = useState({});
    const [currentRole, setCurrentRole] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [showAddRole, setShowAddRole] = useState(false);
    const [newRoleName, setNewRoleName] = useState("");
    const [newRoleDesc, setNewRoleDesc] = useState("");
    const [newRoleActive, setNewRoleActive] = useState(true);
    const [changeCounter, setChangeCounter] = useState({ added: 0, removed: 0 });
    const [initialPrivilegeIds, setInitialPrivilegeIds] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);  // Added state for selected roles

    // Fetch roles and menus
    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/role`).then(res => setRoles(res.data || []));
        axios.get(`${API_BASE_URL}/api/menus`).then(res => setMenus(res.data || []));
    }, []);

    // Fetch privileges for a menu
    const fetchPrivileges = async (menuId) => {
        if (!privileges[menuId]) {
            const res = await axios.get(`${API_BASE_URL}/api/privileges/menu/${menuId}`);
            setPrivileges(prev => ({ ...prev, [menuId]: res.data || [] }));
        }
    };

    // Expand/collapse menu
    const handleMenuExpand = async (menuId) => {
        setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
        await fetchPrivileges(menuId);
    };

    // Handle "Select All" checkbox
    const handleSelectAll = (checked) => {
        setSelectedRoles(
            checked ? roles.map((r) => r.id) : []  // Select/Deselect all roles
        );
    };

    // Handle selecting individual checkboxes
    const handleSingleCheckbox = (roleId) => {
        setSelectedRoles((prev) =>
            prev.includes(roleId)
                ? prev.filter((id) => id !== roleId)  // Deselect the role
                : [...prev, roleId]  // Select the role
        );
    };

    // Select role for editing
    const handleRoleClick = (role) => {
        setCurrentRole(role);
        setEditMode(true);
        setShowAddRole(false);
        const privMap = {};
        (role.privileges || []).forEach(p => { privMap[p.id] = p; });
        setSelectedPrivileges(privMap);
        setInitialPrivilegeIds(role.privileges || [].map(p => p.id));
    };

    // Add new role
    const handleAddRole = () => {
        setShowAddRole(true);
        setEditMode(false);
        setCurrentRole(null);
        setNewRoleName("");
        setNewRoleDesc("");
        setNewRoleActive(true);
        setSelectedPrivileges({});
        setInitialPrivilegeIds([]);
    };

    // Save new or edited role
    const handleSaveRole = async () => {
        const privArr = Object.values(selectedPrivileges);  // Prepare privileges array
        if (showAddRole) {
            await axios.post(`${API_BASE_URL}/api/role`, {
                name: newRoleName,
                description: newRoleDesc,
                active: newRoleActive,
                privileges: privArr
            });
        } else if (editMode && currentRole) {
            await axios.put(`${API_BASE_URL}/api/role/${currentRole.id}`, {
                ...currentRole,
                roleName: currentRole.name,
                roleDescription: currentRole.description,
                active: currentRole.active,
                privileges: privArr
            });
        }

        // Refresh roles after save
        const res = await axios.get(`${API_BASE_URL}/api/role`);
        setRoles(res.data || []);
        setShowAddRole(false);
        setEditMode(false);
        setCurrentRole(null);
        setSelectedPrivileges({});
        setChangeCounter({ added: 0, removed: 0 });
        setExpandedMenus({});
    };

    // Render privilege buttons
    const renderPrivilegeButtons = (privs) => (
        <div className="flex flex-wrap gap-fluid-1">
            {privs.map(priv => (
                <button
                    key={priv.id}
                    type="button"
                    className={`px-fluid-button-x py-fluid-button-y rounded-md border text-fluid-base font-medium transition
            ${selectedPrivileges[priv.id]
                        ? "bg-indigo-600 text-white border-indigo-700"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-indigo-50"}
            relative group
          `}
                    onClick={() => handlePrivilegeToggle(priv)}
                >
                    {priv.action}
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 w-max px-2 py-1 rounded bg-black text-white text-xs opacity-0 group-hover:opacity-100 pointer-events-none z-10">
            {priv.description}
          </span>
                </button>
            ))}
        </div>
    );

    // Privilege toggle
const handlePrivilegeToggle = (priv) => {
    setSelectedPrivileges(prev => {
        const updated = { ...prev };
        if (updated[priv.id]) {
            delete updated[priv.id]; // Deselect the privilege
            setChangeCounter(c => ({ ...c, removed: c.removed + 1 }));
        } else {
            updated[priv.id] = priv; // Select the privilege
            setChangeCounter(c => ({ ...c, added: c.added + 1 }));
        }
        return updated;
    });
};

// Expand all menus
const handleExpandAll = async () => {
    const expanded = {};
    for (const m of menus) {
        expanded[m.id] = true; // Set all menus to expanded
        await fetchPrivileges(m.id); // Fetch privileges for each menu
    }
    setExpandedMenus(expanded); // Update the state
};


    // Helper: get submenus for a menu
    const getSubMenus = (menuId) => menus.filter(m => m.parentId === menuId);

    // Recursive render for menus and submenus
    const renderMenus = (parentId = null, level = 0) => {
        return menus
            .filter(menu => menu.parentId === parentId)
            .map(menu => {
                const hasSubMenus = getSubMenus(menu.id).length > 0;
                return (
                    <div key={menu.id} className={`mb-fluid-2 ml-[${level * 1.5}rem]`}>
                        <div className="flex items-center gap-fluid-1">
                            <button
                                onClick={() => handleMenuExpand(menu.id)}
                                className="focus:outline-none"
                            >
                                {expandedMenus[menu.id] ? <ChevronDown /> : <ChevronRight />}
                            </button>
                            <span className="font-semibold">{menu.displayName || menu.name}</span>
                        </div>
                        {expandedMenus[menu.id] && (
                            <div className="ml-fluid-3">
                                {privileges[menu.id] && privileges[menu.id].length > 0 && (
                                    <div className="mb-fluid-2">
                                        {renderPrivilegeButtons(privileges[menu.id])}
                                    </div>
                                )}
                                {hasSubMenus && (
                                    <div className="flex flex-wrap gap-fluid-3">
                                        {getSubMenus(menu.id).map(subMenu => (
                                            <div key={subMenu.id} className="min-w-[220px]">
                                                <div className="flex items-center gap-fluid-1">
                                                    <button
                                                        onClick={() => handleMenuExpand(subMenu.id)}
                                                        className="focus:outline-none"
                                                    >
                                                        {expandedMenus[subMenu.id] ? <ChevronDown /> : <ChevronRight />}
                                                    </button>
                                                    <span className="font-semibold">{subMenu.displayName || subMenu.name}</span>
                                                </div>
                                                {expandedMenus[subMenu.id] && (
                                                    <div className="ml-fluid-3">
                                                        {privileges[subMenu.id] && privileges[subMenu.id].length > 0 && (
                                                            <div className="mb-fluid-2">
                                                                {renderPrivilegeButtons(privileges[subMenu.id])}
                                                            </div>
                                                        )}
                                                        {getSubMenus(subMenu.id).length > 0 && (
                                                            <div>{renderMenus(subMenu.id, level + 1)}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            });
    };

    return (
        <div className="flex w-full h-full bg-white rounded-lg shadow border border-gray-200">
            {/* Left: Roles */}
            <aside className="flex w-[30%] flex-col border-r border-gray-200 bg-white">
                <div className="flex items-center justify-between p-fluid-2 border-b border-gray-200">
                    <h2 className="text-fluid-lg font-bold">Roles</h2>
                    <button
                        className="bg-indigo-600 text-white px-fluid-2 py-fluid-1 rounded font-medium"
                        onClick={handleAddRole}
                    >
                        Add Role
                    </button>
                </div>
                <nav className="flex-1 overflow-y-auto">
                    {roles.map(role => (
                        <button
                            key={role.id}
                            className={`w-full text-left px-fluid-2 py-fluid-1 flex items-center gap-fluid-1 border-b border-gray-100
                                ${currentRole?.id === role.id && !showAddRole ? "bg-indigo-50" : ""}
                                ${!role.active ? "opacity-50" : ""}
                                hover:bg-indigo-50 transition`}
                                            onClick={() => handleRoleClick(role)}
                                        >
                            <span className="font-semibold">{role.name}</span>
                            <span className="ml-auto text-xs text-gray-500">
                                {role.privileges ? role.privileges.length : 0} privileges
                            </span>
                            <span className={`ml-2 text-xs rounded px-2 py-0.5 ${role.active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}>
                                {role.active ? "Active" : "Inactive"}
                            </span>
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Right: Privileges */}
            <main className="flex w-[100%] flex-col">
                <div className="flex items-center justify-between border-b border-gray-200 bg-white p-fluid-2">
                    <div>
                        <h1 className="text-fluid-xl font-bold text-gray-900">
                            {showAddRole ? "Add New Role" : currentRole?.name || "Select a Role"}
                        </h1>
                        <p className="text-fluid-base text-gray-600">
                            {showAddRole ? "Create a new role and assign privileges." : currentRole?.description}
                        </p>
                    </div>
                    <div className="flex items-center gap-fluid-2">
                        <button
                            className="rounded-md border border-indigo-600 text-indigo-700 px-3 py-1.5 text-fluid-base font-medium hover:bg-indigo-50"
                            onClick={handleExpandAll}
                        >
                            Expand All
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto bg-white p-fluid-3">
                    {(showAddRole || editMode) && (
                        <form
                            className="mb-fluid-3 flex flex-col gap-fluid-2"
                            onSubmit={e => { e.preventDefault(); handleSaveRole(); }}
                        >
                            <div className="flex gap-fluid-2">
                                <input
                                    className="border rounded p-fluid-1 flex-1"
                                    placeholder="Role Name"
                                    value={showAddRole ? newRoleName : currentRole?.name || ""}
                                    onChange={e => showAddRole
                                        ? setNewRoleName(e.target.value)
                                        : setCurrentRole(r => ({ ...r, name: e.target.value }))
                                    }
                                    required
                                />
                                <input
                                    className="border rounded p-fluid-1 flex-1"
                                    placeholder="Role Description"
                                    value={showAddRole ? newRoleDesc : currentRole?.description || ""}
                                    onChange={e => showAddRole
                                        ? setNewRoleDesc(e.target.value)
                                        : setCurrentRole(r => ({ ...r, description: e.target.value }))
                                    }
                                    required
                                />
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={showAddRole ? newRoleActive : currentRole?.active}
                                        onChange={e => showAddRole
                                            ? setNewRoleActive(e.target.checked)
                                            : setCurrentRole(r => ({ ...r, active: e.target.checked }))
                                        }
                                    />
                                    <span className="text-fluid-base">Active</span>
                                </label>
                            </div>
                            <button
                                type="submit"
                                className="bg-indigo-600 text-white rounded px-fluid-2 py-fluid-1 w-max self-end"
                            >
                                Save
                            </button>
                        </form>
                    )}
                    {/* Privilege Matrix */}
                    <div className="space-y-fluid-3">
                        {renderMenus()}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default RoleManagement;
